import { useRef, useState } from 'react';
import { Mail, Phone, MapPin, Briefcase, Building2, Calendar, Users, Camera } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import {
  useEmployee,
  useMyBalance,
  useInitPhotoUpload,
  useConfirmPhoto,
} from '@/lib/queries';
import { initials } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const TABS = ['Datos Generales', 'Contacto', 'Vacaciones', 'Cumplimiento'] as const;

type Tab = (typeof TABS)[number];

export function MyProfilePage() {
  const user = useAuthStore((s) => s.user);
  const { data: e } = useEmployee(user?.employeeId);
  const { data: balance } = useMyBalance();
  const [tab, setTab] = useState<Tab>('Datos Generales');
  const fileRef = useRef<HTMLInputElement>(null);
  const initPhoto = useInitPhotoUpload();
  const confirmPhoto = useConfirmPhoto();
  const [uploading, setUploading] = useState(false);

  const handlePhoto = async (file: File) => {
    if (!user?.employeeId) return;
    setUploading(true);
    try {
      const { uploadUrl, s3Key } = await initPhoto.mutateAsync({
        employeeId: user.employeeId,
        payload: { fileName: file.name, mimeType: file.type, size: file.size },
      });
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!res.ok) throw new Error('upload failed');
      await confirmPhoto.mutateAsync({ employeeId: user.employeeId, s3Key });
    } catch (err) {
      alert('No se pudo subir la foto');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!e)
    return <p className="py-10 text-center text-font-secondary">Cargando...</p>;

  const fullName = `${e.firstName} ${e.lastName}`;

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header card */}
      <FadeIn>
        <Card className="overflow-hidden rounded-xl">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-5">
              {e.photoUrl ? (
                <img
                  src={e.photoUrl}
                  alt={fullName}
                  className="h-20 w-20 flex-shrink-0 rounded-full object-cover ring-2 ring-primary-subtle"
                />
              ) : (
                <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-2xl font-semibold text-white ring-2 ring-primary-subtle">
                  {initials(fullName)}
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-font-primary">{fullName}</h1>
                <p className="mt-1 text-sm text-font-secondary">
                  {e.position?.title ?? '–'} · {e.country?.nameEs ?? '–'}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <Badge variant="success" dot>
                    Activo
                  </Badge>
                  <span className="text-xs text-font-tertiary">
                    Desde {new Date(e.hireDate).toLocaleDateString('es')} · #
                    {e.employeeNumber}
                  </span>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(ev) => {
                  const f = ev.target.files?.[0];
                  if (f) handlePhoto(f);
                }}
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                <Camera size={16} />
                {uploading ? 'Subiendo...' : 'Cambiar foto'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </FadeIn>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="overflow-hidden rounded-xl lg:col-span-2">
          <div className="flex flex-wrap gap-1 border-b border-border px-6">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative px-4 py-4 text-sm font-semibold transition ${
                  tab === t ? 'text-primary' : 'text-font-secondary hover:text-font-primary'
                }`}
              >
                {t}
                {tab === t && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {tab === 'Datos Generales' && (
              <div>
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-font-secondary">
                  Información Personal
                </h3>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <IconField icon={Briefcase} label="Puesto" value={e.position?.title} />
                  <IconField icon={Building2} label="Departamento" value={e.department?.name} />
                  <IconField icon={Calendar} label="Fecha de ingreso" value={new Date(e.hireDate).toLocaleDateString('es')} />
                  <IconField icon={MapPin} label="País" value={e.country?.nameEs} />
                  <IconField
                    icon={Users}
                    label="Jefe directo"
                    value={
                      e.manager ? `${e.manager.firstName} ${e.manager.lastName}` : '–'
                    }
                  />
                  <IconField icon={Briefcase} label="Modalidad" value={e.workMode ?? 'Presencial'} />
                </div>
              </div>
            )}

            {tab === 'Contacto' && (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <IconField icon={Mail} label="Correo corporativo" value={e.corporateEmail} />
                <IconField icon={Mail} label="Correo personal" value={e.personalEmail} />
                <IconField icon={Phone} label="Teléfono" value={e.phone} />
                <IconField icon={MapPin} label="Dirección" value={e.address} />
              </div>
            )}

            {tab === 'Vacaciones' && (
              <div className="flex flex-col gap-4">
                <p className="text-sm text-font-secondary">
                  Consulta tu saldo de vacaciones y el historial de tus solicitudes en
                  la sección <strong>Permisos y Vacaciones</strong>.
                </p>
              </div>
            )}

            {tab === 'Cumplimiento' && (
              <div className="space-y-3">
                <CompRow
                  label="Código de conducta firmado"
                  ok={!!e.codeOfConductSigned}
                />
                <CompRow
                  label="Políticas internas firmadas"
                  ok={!!e.policiesSigned}
                />
                <CompRow
                  label="Capacitaciones completadas"
                  ok={!!e.trainingsCompleted}
                />
                {e.complianceNotes && (
                  <p className="mt-2 text-xs text-font-secondary">
                    <strong>Notas:</strong> {e.complianceNotes}
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Sidebar: balance + contact */}
        <div className="flex flex-col gap-6">
          <Card hover className="rounded-xl">
            <CardContent className="p-5">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-font-secondary">
                Saldo de Vacaciones
              </h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-primary">
                    {balance?.available ?? 15}
                  </p>
                  <p className="text-[11px] text-font-secondary">Disponibles</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-font-primary">
                    {balance?.taken ?? 3}
                  </p>
                  <p className="text-[11px] text-font-secondary">Tomados</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-warning">
                    {balance?.pending ?? 2}
                  </p>
                  <p className="text-[11px] text-font-secondary">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card hover className="rounded-xl">
            <CardContent className="p-5">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-font-secondary">
                Contacto de Emergencia
              </h3>
              {e.emergencyContact ? (
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-font-primary">
                    {e.emergencyContact}
                  </p>
                  <p className="text-font-secondary">
                    <Phone size={12} className="mr-1 inline" />
                    {e.emergencyPhone ?? '–'}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-font-tertiary">Sin contacto registrado.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageTransition>
  );
}

function CompRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border p-3 transition hover:border-border-light hover:bg-bg">
      <span className="text-sm font-semibold text-font-primary">{label}</span>
      <Badge variant={ok ? 'success' : 'warning'} dot>
        {ok ? 'OK' : 'Pendiente'}
      </Badge>
    </div>
  );
}

function IconField({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary-subtle text-primary">
        <Icon size={16} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-font-tertiary">
          {label}
        </p>
        <p className="mt-0.5 text-sm text-font-primary">{value || '–'}</p>
      </div>
    </div>
  );
}
